/// <reference types="w3c-web-usb" />
import { fieldConfigs } from './config/fields';
import { TextFieldConfig, NumberFieldConfig, SelectFieldConfig, CheckboxFieldConfig } from './config/fieldTypes';
import type { ConfigMap } from './config/ConfigStore';
import type { PairedDevice, ConnectionSettings } from './usb/usb';
import { t } from './intl';

const CONNECTION_BAUD_OPTIONS = [9600, 19200, 38400, 57600, 115200];

function renderDisconnected(
  pairedDevices: PairedDevice[],
  settings: ConnectionSettings,
): string {
  const pairedListHtml = pairedDevices.length > 0
    ? `
      <div class="paired-devices">
        <h2>${t('disconnected.previouslyConnected')}</h2>
        <ul class="device-list">
          ${pairedDevices.map((d, i) => `
            <li class="device-item" data-device-index="${i}">
              <div class="device-info-compact">
                <span class="device-name">${d.productName}</span>
                <span class="device-manufacturer">${d.manufacturer}</span>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </li>
          `).join('')}
        </ul>
      </div>
    `
    : '';

  const baudOptionsHtml = CONNECTION_BAUD_OPTIONS.map(rate =>
    `<option value="${rate}" ${rate === settings.baudRate ? 'selected' : ''}>${rate}</option>`
  ).join('');

  return `
    <div class="fields-column">
      <h1>${t('app.title')}</h1>
      <div class="form-group">
        <label for="connection-baud-rate">${t('disconnected.connectionBaudRate')}</label>
        <select id="connection-baud-rate">
          ${baudOptionsHtml}
        </select>
      </div>
      ${pairedListHtml}
      <div class="connect-actions">
        <button id="connect-btn" class="btn btn-primary">
          <span class="btn-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 8a6 6 0 0 1 12 0v4a6 6 0 0 1-12 0V8z"/>
              <path d="M12 14v6"/>
              <path d="M8 20h8"/>
            </svg>
          </span>
          ${t('disconnected.connectNewDevice')}
        </button>
      </div>
    </div>
  `;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

interface AppElements {
  app: HTMLElement;
}

export interface AppState {
  state: ConnectionState;
  config: ConfigMap;
  elements: AppElements;
  error: string | null;
  deviceInfo: { name: string; manufacturer: string } | null;
  pairedDevices: PairedDevice[];
  connectionSettings: ConnectionSettings;
  usbDevice: USBDevice | null;
  onDeviceDisconnect: (() => void) | null;
}

function renderField(config: ConfigMap, field: TextFieldConfig | NumberFieldConfig | SelectFieldConfig | CheckboxFieldConfig): string {
  const currentValue = config.get(field.register);
  const value = currentValue !== undefined ? currentValue : field.defaultValue;

  switch (field.type) {
    case 'text':
      return renderTextField(field, value as string);
    case 'number':
      return renderNumberField(field, value as number);
    case 'select':
      return renderSelectField(field, value as string | number);
    case 'checkbox':
      return renderCheckboxField(field, value as boolean);
  }
}

function renderTextField(field: TextFieldConfig, value: string): string {
  return `
    <div class="form-group">
      <label for="${field.register}">${t(field.name)}</label>
      <input
        type="text"
        id="${field.register}"
        placeholder="${field.placeholder ? t(field.placeholder) : ''}"
        value="${value}"
      >
    </div>
  `;
}

function renderNumberField(field: NumberFieldConfig, value: number): string {
  return `
    <div class="form-group">
      <label for="${field.register}">${t(field.name)}</label>
      <input
        type="number"
        id="${field.register}"
        min="${field.min ?? 0}"
        max="${field.max ?? 100}"
        step="${field.step ?? 1}"
        value="${value}"
      >
    </div>
  `;
}

function renderSelectField(field: SelectFieldConfig, value: string | number): string {
  const optionsHtml = field.options
    .map((opt) => `<option value="${opt.value}" ${opt.value === value ? 'selected' : ''}>${t(opt.label)}</option>`)
    .join('');

  return `
    <div class="form-group">
      <label for="${field.register}">${t(field.name)}</label>
      <select id="${field.register}">
        ${optionsHtml}
      </select>
    </div>
  `;
}

function renderCheckboxField(field: CheckboxFieldConfig, value: boolean): string {
  return `
    <div class="form-group checkbox">
      <label for="${field.register}">${t(field.name)}</label>
      <input
        type="checkbox"
        id="${field.register}"
        ${value ? 'checked' : ''}
      >
    </div>
  `;
}

function renderConnecting(): string {
  return `
    <div class="fields-column">
      <h1>${t('app.title')}</h1>
      <div class="connecting-state">
        <div class="spinner"></div>
        <p class="connecting-text">${t('connecting.selectDevice')}</p>
      </div>
    </div>
  `;
}

function renderError(message: string): string {
  return `
    <div class="fields-column">
      <h1>${t('app.title')}</h1>
      <div class="error-state">
        <div class="error-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <p class="error-message">${message}</p>
        <button id="retry-btn" class="btn btn-secondary">${t('error.tryAgain')}</button>
        <button id="back-btn" class="btn btn-outline">${t('error.back')}</button>
      </div>
    </div>
  `;
}

function renderConnected(config: ConfigMap, deviceInfo: { name: string; manufacturer: string }): string {
  const fieldsHtml = fieldConfigs.map((field) => renderField(config, field)).join('');

  return `
    <div class="fields-column">
      <div class="device-info">
        <span class="device-status connected-badge">${t('connected.status')}</span>
        <span class="device-name">${deviceInfo.name}</span>
        <span class="device-manufacturer">${deviceInfo.manufacturer}</span>
      </div>
      <div class="serial-input-group">
        <input type="text" id="serial-input" placeholder="${t('connected.serialPlaceholder')}">
        <button id="serial-send-btn" class="btn btn-outline">${t('connected.send')}</button>
      </div>
      ${fieldsHtml}
    </div>
    <div class="help-column">
      <p id="help-text">${t('connected.helpText')}</p>
    </div>
    <div class="action-bar">
      <button id="disconnect-btn" class="btn btn-outline">${t('connected.disconnect')}</button>
      <div class="action-spacer"></div>
      <button id="export-btn" class="btn btn-secondary">${t('connected.exportToFile')}</button>
      <button id="save-btn" class="btn btn-primary">${t('connected.saveToDevice')}</button>
    </div>
  `;
}

export function render(app: AppState): void {
  let html: string;
  switch (app.state) {
    case 'disconnected':
      html = renderDisconnected(app.pairedDevices, app.connectionSettings);
      break;
    case 'connecting':
      html = renderConnecting();
      break;
    case 'error':
      html = renderError(app.error ?? 'An unknown error occurred');
      break;
    case 'connected':
      html = renderConnected(app.config, app.deviceInfo!);
      break;
  }
  app.elements.app.innerHTML = html;
}
